import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BookOpen, GraduationCap, Mic } from "lucide-react";
import client from "../lib/api";
import { trackAmazonClick } from "../lib/tracking";
import SEO from "../components/SEO";
import Divider from "../components/Divider";
import Marquee from "../components/Marquee";
import Counters from "../components/Counters";
import LeadMagnetForm from "../components/LeadMagnetForm";
import TestimonialSlider from "../components/TestimonialSlider";
import CourseGallery from "../components/CourseGallery";
import BookMockup from "../components/BookMockup";
import WineAdvisor from "../components/WineAdvisor";
import { Reveal, MaskedLines } from "../components/Reveal";

const IMGS = {
  hero: "https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHxfMTc4NzI2MDA2Nnw&q=85",
  stefano: "/assets/stefano.jpg",
  cellar: "https://images.pexels.com/photos/774454/pexels-photo-774454.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  pour: "https://images.pexels.com/photos/24748876/pexels-photo-24748876.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  vineyard: "https://images.unsplash.com/photo-1585553616435-2dc0a54e271d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHxfMTc4NzI2MDA2Nnw&q=85",
};

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [articles, setArticles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [amazon, setAmazon] = useState("https://amzn.eu/d/01pW9tnS");
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 160]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.3]);

  useEffect(() => {
    client.get("/courses").then((r) => setCourses(r.data.slice(0, 3))).catch(() => {});
    client.get("/episodes").then((r) => setEpisodes(r.data.slice(0, 3))).catch(() => {});
    client.get("/articles").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
    client.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    client.get("/settings/public").then((r) => setAmazon(r.data.amazon_url)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO path="/" description="Corsi, podcast e racconti per chi vuole capire il vino senza snobismi. Da 30 anni dentro le cantine, accanto a chi il vino lo fa." />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center bg-nero overflow-hidden grain">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0">
          <video className="h-full w-full object-cover opacity-50" autoPlay muted loop playsInline
                 preload="metadata" poster="/assets/hero-pour.jpg" aria-hidden="true" data-testid="hero-video">
            <source src="/assets/hero.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-nero via-nero/60 to-nero/40" />
        </motion.div>
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 pt-32 pb-24 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-12 items-center">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="text-oro text-xs sm:text-sm uppercase tracking-[0.4em] mb-8"
              >
                L'Influexpert del Vino
              </motion.p>
              <h1 className="font-serif font-bold text-panna text-4xl sm:text-5xl lg:text-6xl leading-[1.08] max-w-2xl">
                <MaskedLines lines={["Storie di vino,", "persone e", "passione."]} delay={0.35} />
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.9 }}
                className="mt-8 text-panna/75 text-base md:text-lg leading-relaxed max-w-xl"
              >
                Corsi, podcast e racconti per chi vuole capire il vino senza snobismi.
                Da 30 anni dentro le cantine, accanto a chi il vino lo fa.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.9 }}
                className="mt-10 flex flex-wrap gap-4"
              >
                <Link to="/il-libro" data-testid="hero-cta-book"
                      className="group rounded-full bg-bordeaux px-8 py-4 font-medium text-panna border border-oro/30 transition-colors duration-300 hover:bg-bordeaux-dark inline-flex items-center gap-2">
                  Scopri il libro
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <motion.div animate={{ scale: [1, 1.045, 1] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
                  <Link to="/corsi/corso-base-il-vino-dalla-vigna-al-calice" data-testid="hero-cta-offerta"
                        className="group relative rounded-full bg-oro px-8 py-4 font-semibold text-nero shadow-[0_0_28px_rgba(198,160,74,0.55)] inline-flex items-center gap-2.5 hover:brightness-105 transition-[filter] duration-300">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bordeaux opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-bordeaux" />
                    </span>
                    Offerta lancio: Corso Base
                    <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </motion.div>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="hidden lg:block"
            >
              <img src="/assets/logo-dark.png" alt="Logo Dentro il Calice: volto con coppola e occhiali in un cerchio bordeaux"
                   className="w-72 xl:w-80 h-auto drop-shadow-2xl" />
            </motion.div>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-panna/40 text-xs tracking-[0.3em] uppercase"
        >
          Scorri
        </motion.div>
      </section>

      <Marquee light />

      {/* CHI È STEFANO */}
      <section className="bg-panna py-24 sm:py-32" data-testid="section-chi-e-stefano">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <Reveal>
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full rounded-2xl border-2 border-oro/40" />
                <img src={IMGS.stefano} alt="Stefano Cremonese durante una degustazione in cantina"
                     className="relative rounded-2xl object-cover aspect-[4/5] w-full shadow-xl" loading="lazy" />
              </div>
            </Reveal>
            <div>
              <Reveal>
                <p className="text-oliva text-xs uppercase tracking-[0.35em] mb-4">Chi è Stefano</p>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-bordeaux leading-tight">
                  Trent'anni di strada, cantine e ristoranti.
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-6 text-nero/80 leading-relaxed">
                  Agente di commercio vino e spirits dal 1996 nel canale Horeca, figlio d'arte — il padre Elio
                  agente dal 1967. Oltre 500 studenti formati, divulgatore e "Wine Influexpert": uno che il vino
                  non lo racconta dai manuali, ma dal banco dei ristoranti dove lo vende ogni giorno.
                </p>
                <Link to="/chi-sono" data-testid="link-la-mia-storia"
                      className="mt-8 inline-flex items-center gap-2 text-bordeaux font-medium group">
                  La mia storia
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* NUMERI */}
      <section className="bg-nero py-24 sm:py-28 grain relative" data-testid="section-numbers">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Counters />
        </div>
      </section>

      {/* IL LIBRO */}
      <section className="bg-panna py-24 sm:py-32" data-testid="section-libro">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal className="order-2 lg:order-1">
              <div className="py-8"><BookMockup /></div>
            </Reveal>
            <div className="order-1 lg:order-2">
              <Reveal>
                <p className="text-oro text-xs uppercase tracking-[0.35em] mb-4">Il Libro</p>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-bordeaux leading-tight">
                  Dentro il Calice – Italia
                </h2>
                <p className="mt-5 text-nero/80 leading-relaxed">
                  Il viaggio regione per regione nel vino italiano, raccontato da chi lo vende ogni giorno
                  ai ristoranti — non da un manuale.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <ul className="mt-7 space-y-3">
                  {["Tutte le regioni, i vitigni e le denominazioni che contano",
                    "Abbinamenti, errori comuni e aneddoti dalle cantine",
                    "Scritto come si parla: zero paroloni, tanta sostanza"].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-nero/85">
                      <span className="text-oro mt-1 text-xs">◆</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap items-center gap-5">
                  <a href={amazon} target="_blank" rel="noopener noreferrer"
                     onClick={() => trackAmazonClick("home_section")}
                     data-testid="amazon-buy-button"
                     className="rounded-full bg-bordeaux px-8 py-4 font-medium text-panna border border-oro/30 hover:bg-bordeaux-dark transition-colors">
                    Compralo su Amazon — €14,99
                  </a>
                  <Link to="/il-libro" data-testid="link-scopri-libro" className="text-bordeaux font-medium inline-flex items-center gap-2 group">
                    Scopri di più
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CORSI */}
      <section className="bg-nero py-24 sm:py-32 grain relative" data-testid="section-corsi">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="text-center mb-16">
            <p className="text-oro text-xs uppercase tracking-[0.35em] mb-4">L'ecosistema formativo</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna">I Corsi</h2>
            <Divider light className="mt-8" />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {courses.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.12}>
                <Link to={`/corsi/${c.slug}`} data-testid={`course-card-${c.slug}`}
                      className="group block h-full rounded-2xl bg-nero-light border border-panna/10 p-8 transition-all duration-500 hover:border-oro/40 hover:-translate-y-1.5">
                  <div className="flex items-center justify-between mb-6">
                    <GraduationCap size={22} className="text-oro" />
                    <span className={`text-[0.65rem] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full ${
                      c.status === "disponibile" ? "bg-oliva/20 text-oliva border border-oliva/40" : "bg-oro/10 text-oro border border-oro/30"
                    }`}>
                      {c.status === "disponibile" ? "Disponibile" : "In arrivo"}
                    </span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-panna leading-snug group-hover:text-oro transition-colors">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-sm text-panna/60 leading-relaxed line-clamp-3">{c.description}</p>
                  <div className="mt-6 flex items-center justify-between text-sm">
                    <span className="text-panna/50">{c.num_lessons} lezioni · {c.target}</span>
                    <span className="font-serif text-oro font-semibold">€{c.price.toFixed(0)}</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-14">
            <Link to="/corsi" data-testid="cta-tutti-i-corsi"
                  className="inline-flex items-center gap-2 rounded-full border border-oro/60 px-8 py-4 text-oro font-medium hover:bg-oro/10 transition-colors">
              Vedi tutti i corsi <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PODCAST */}
      <section className="bg-panna py-24 sm:py-32" data-testid="section-podcast">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-14 items-center">
            <Reveal>
              <p className="text-oliva text-xs uppercase tracking-[0.35em] mb-4">Il Podcast</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-bordeaux leading-tight">
                Dentro il Calice — Il Podcast
              </h2>
              <p className="mt-5 text-nero/80 leading-relaxed">
                Video podcast di 30 minuti. Un ospite, un calice, una storia vera.
              </p>
              <div className="mt-8 rounded-2xl bg-nero aspect-video flex items-center justify-center relative overflow-hidden group">
                <img src={IMGS.cellar} alt="Cantina con botti in legno, atmosfera calda" className="absolute inset-0 object-cover opacity-50" loading="lazy" />
                <div className="relative z-10 text-center px-6">
                  <Mic size={28} className="text-oro mx-auto mb-3" />
                  <p className="text-panna/80 text-sm">Primo episodio in arrivo</p>
                  <p className="text-panna/50 text-xs mt-1">Player YouTube / Spotify in integrazione</p>
                </div>
              </div>
            </Reveal>
            <div className="space-y-5">
              {episodes.map((e, i) => (
                <Reveal key={e.id} delay={i * 0.1}>
                  <Link to="/podcast" data-testid={`episode-card-${e.number}`}
                        className="flex gap-5 items-center rounded-2xl border border-nero/10 bg-panna-dark/40 p-5 transition-all duration-300 hover:border-bordeaux/30 hover:bg-panna-dark/70">
                    <div className="h-16 w-16 rounded-xl bg-bordeaux text-panna font-serif text-xl font-bold flex items-center justify-center shrink-0">
                      {String(e.number).padStart(2, "0")}
                    </div>
                    <div className="min-w-0">
                      <span className="text-[0.65rem] uppercase tracking-[0.2em] text-oliva">{e.series}</span>
                      <h3 className="font-serif font-semibold text-nero leading-snug truncate">{e.title}</h3>
                      <p className="text-xs text-nero/50 mt-1">{e.duration} · {e.guest}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
              <Reveal delay={0.3}>
                <Link to="/podcast" data-testid="cta-tutti-episodi" className="inline-flex items-center gap-2 text-bordeaux font-medium group">
                  Tutti gli episodi
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* LEAD MAGNET */}
      <section id="guida-gratuita" className="bg-bordeaux py-24 sm:py-32 grain relative" data-testid="section-lead-magnet">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-14 items-center">
            <Reveal>
              <div className="relative mx-auto w-48 sm:w-56">
                <div className="aspect-[1/1.414] rounded-lg bg-nero border border-oro/30 shadow-2xl p-6 flex flex-col">
                  <p className="text-oro text-[0.6rem] tracking-[0.3em] uppercase">Guida gratuita</p>
                  <p className="font-serif text-panna text-2xl font-bold mt-3 leading-tight">7 Errori Comuni</p>
                  <p className="text-panna/60 text-xs mt-2 leading-relaxed">che (quasi) tutti commettono quando bevono vino</p>
                  <div className="mt-auto h-px bg-oro/40" />
                  <p className="text-panna/40 text-[0.55rem] mt-2 tracking-widest uppercase">Dentro il Calice</p>
                </div>
                <div className="absolute -bottom-4 -right-4 w-full h-full rounded-lg border border-oro/30 -z-10" />
              </div>
            </Reveal>
            <div>
              <Reveal>
                <p className="text-oro text-xs uppercase tracking-[0.35em] mb-4">PDF gratuito · 12 pagine</p>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna leading-tight">
                  7 Errori Comuni che (quasi) tutti commettono quando bevono vino
                </h2>
                <p className="mt-5 text-panna/80 leading-relaxed">
                  Una guida gratuita di 12 pagine per smettere di sbagliare e iniziare a goderti davvero ogni calice.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="mt-8">
                <LeadMagnetForm dark source="home" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CONSIGLIAMI UN VINO (AI) */}
      <section className="bg-nero py-24 sm:py-32 grain relative" data-testid="section-wine-advisor">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <WineAdvisor />
        </div>
      </section>

      {/* TESTIMONIANZE */}
      <section className="bg-panna py-24 sm:py-32" data-testid="section-testimonianze">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="text-center mb-16">
            <p className="text-oliva text-xs uppercase tracking-[0.35em] mb-4">Dicono di noi</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-bordeaux">Le voci degli studenti</h2>
            <Divider className="mt-8" />
          </Reveal>
          {testimonials.length > 0 && <TestimonialSlider testimonials={testimonials} />}

          <Reveal className="mt-20">
            <div className="text-center mb-10">
              <p className="text-oliva text-xs uppercase tracking-[0.35em] mb-4">Momenti dai corsi</p>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-bordeaux">Dentro l'aula, dentro il calice</h3>
              <p className="mt-4 text-nero/60 text-sm max-w-xl mx-auto">Momenti reali dai corsi di degustazione: le persone, i tavoli, i brindisi.</p>
            </div>
            <CourseGallery />
          </Reveal>
        </div>
      </section>

      {/* STORIE */}
      <section className="bg-nero py-24 sm:py-32 grain relative" data-testid="section-storie">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <div>
              <p className="text-oro text-xs uppercase tracking-[0.35em] mb-4">Il Blog</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna">Storie dal calice</h2>
            </div>
            <Link to="/storie" data-testid="cta-tutte-le-storie" className="inline-flex items-center gap-2 text-oro group font-medium">
              Tutte le storie <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {articles.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.12}>
                <Link to={`/storie/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group block">
                  <div className="rounded-2xl overflow-hidden mb-5 aspect-[16/10] relative">
                    <img src={a.image || [IMGS.pour, IMGS.vineyard, IMGS.cellar][i % 3]}
                         alt={a.title} loading="lazy"
                         className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <span className="text-[0.65rem] uppercase tracking-[0.2em] text-oliva">{a.category} · {a.reading_time} min</span>
                  <h3 className="font-serif text-xl font-semibold text-panna mt-2 leading-snug group-hover:text-oro transition-colors">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-sm text-panna/55 leading-relaxed line-clamp-2">{a.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CITAZIONE FINALE */}
      <section className="bg-bordeaux py-24 grain relative" data-testid="section-brand-quote">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <Reveal>
            <BookOpen size={24} className="text-oro mx-auto mb-8" />
            <p className="font-script text-3xl sm:text-4xl lg:text-5xl text-panna leading-snug">
              "Il vino è fatto dall'uomo, ognuno ha la sua storia personale, non è un prodotto standard.
              Stai bevendo storia, sacrificio e amore per la vite."
            </p>
            <p className="mt-8 text-sm uppercase tracking-[0.3em] text-oro">Stefano Cremonese</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
